import React, { useContext } from 'react';
import { Form, FormRow, ValidInput, Tile, Button, Row, Col } from '@op/opds.opux';
import message from '../../messages';
import { AdditionalSignatoryData, ContextWrapper } from '../../context/ContextWrapper';
import { isValidTextInput, isValidPhoneInput, isValidEmailInput } from '../../validation/Validation';
import { IconDelete } from '@op/opds.opux-icons/action';
import { deepCopy } from '../../util/Util';
import { id } from '../../Constants';

interface AdditionalSignatoryFormProps {
    signatory: AdditionalSignatoryData;
    // eslint-disable-next-line no-unused-vars
    updateInformation: (signatoryInfo: AdditionalSignatoryData) => void;
    enableDeleteButton: boolean;
    removeForm: () => void;
}

const AdditionalSignatoryForm = React.forwardRef<HTMLDivElement, AdditionalSignatoryFormProps>(
    ({ signatory, updateInformation, enableDeleteButton, removeForm }, ref) => {
        const { contractData } = useContext(ContextWrapper);
        const checkValidation: boolean = contractData.checkValidation;
        const signatoryData: AdditionalSignatoryData = deepCopy(signatory);

        const firstNameValidation = isValidTextInput(signatoryData.firstName, checkValidation);
        const lastNameValidation = isValidTextInput(signatoryData.lastName, checkValidation);
        const phoneValidation = isValidPhoneInput(signatoryData.phoneNumber, checkValidation);
        const emailValidation = isValidEmailInput(signatoryData.emailAddress, checkValidation);

        const updateField = (field: keyof AdditionalSignatoryData, value: string) => {
            const updated = { ...signatoryData, [field]: value };
            updateInformation(updated);
        };

        return (
            <div ref={ref}>
                <Tile data-testid="additional-signatory-tile">
                    <Row>
                        <Col xs={11}>
                            <h2>{message('additionalSignatoryFormHeader')}</h2>
                            <div>{message('additionalSignatoryFormDescription')}</div>
                        </Col>
                        {enableDeleteButton && (
                            <Col className="eservice-textalign-right" xs={1}>
                                <Button
                                    icon={IconDelete}
                                    iconLabel="Delete item"
                                    minified
                                    compact
                                    onClick={removeForm}
                                    data-testid="removeAdditionalSignatoryButton"
                                />
                            </Col>
                        )}
                    </Row>
                    <Form visualizeRequiredFields data-testid="additional-signatory-form">
                        <FormRow label={message('firstName')} required data-testid="additionalFirstName">
                            <ValidInput
                                className={id.ADDITIONAL_SIGNATORY_FIRST_NAME}
                                value={signatoryData.firstName}
                                maxLength={50}
                                data-testid="additionalFirstNameInput"
                                onChange={(event: any) => updateField('firstName', event.target.value)}
                                error={firstNameValidation.message}
                                invalid={firstNameValidation.notValid}
                            />
                        </FormRow>
                        <FormRow label={message('lastName')} required data-testid="additionalLastName">
                            <ValidInput
                                className={id.ADDITIONAL_SIGNATORY_LAST_NAME}
                                value={signatoryData.lastName}
                                maxLength={50}
                                data-testid="additionalLastNameInput"
                                onChange={(event: any) => updateField('lastName', event.target.value)}
                                error={lastNameValidation.message}
                                invalid={lastNameValidation.notValid}
                            />
                        </FormRow>
                        <FormRow label={message('phone')} required data-testid="additionalPhone">
                            <ValidInput
                                className={id.ADDITIONAL_SIGNATORY_PHONE}
                                value={signatoryData.phoneNumber}
                                maxLength={20}
                                data-testid="additionalPhoneInput"
                                onChange={(event: any) => updateField('phoneNumber', event.target.value)}
                                error={phoneValidation.message}
                                invalid={phoneValidation.notValid}
                            />
                        </FormRow>
                        <FormRow label={message('emailAddress')} required data-testid="additionalEmail">
                            <ValidInput
                                className={id.ADDITIONAL_SIGNATORY_EMAIL}
                                value={signatoryData.emailAddress}
                                maxLength={100}
                                data-testid="additionalEmailInput"
                                onChange={(event: any) => updateField('emailAddress', event.target.value)}
                                error={emailValidation.message}
                                invalid={emailValidation.notValid}
                            />
                        </FormRow>
                    </Form>
                </Tile>
            </div>
        );
    }
);

export default AdditionalSignatoryForm;
